/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import AgencyAdminRegionEntity from 'Models/Security/Acl/AgencyAdminRegionEntity';
import ApplicationUserRegionEntity from 'Models/Security/Acl/ApplicationUserRegionEntity';
import AgencyPlaylisterRegionEntity from 'Models/Security/Acl/AgencyPlaylisterRegionEntity';
import VisitorsRegionEntity from 'Models/Security/Acl/VisitorsRegionEntity';
import MustardAdminRegionEntity from 'Models/Security/Acl/MustardAdminRegionEntity';
import MustardManagementRegionEntity from 'Models/Security/Acl/MustardManagementRegionEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IRegionEntityAttributes extends IModelAttributes {
	name: string;

	countriess: Array<
		| Models.CountryEntity
		| Models.ICountryEntityAttributes
	>;
	venuess: Array<
		| Models.VenueEntity
		| Models.IVenueEntityAttributes
	>;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('RegionEntity', 'Region')
// % protected region % [Customise your entity metadata here] end
export default class RegionEntity extends Model
	implements IRegionEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new AgencyAdminRegionEntity(),
		new ApplicationUserRegionEntity(),
		new AgencyPlaylisterRegionEntity(),
		new VisitorsRegionEntity(),
		new MustardAdminRegionEntity(),
		new MustardManagementRegionEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	/**
	 * The default order by field when the collection is loaded .
	 */
	public get orderByField(): IOrderByCondition<Model> | undefined {
		// % protected region % [Modify the order by field here] off begin
		return {
			path: 'name',
			descending: false,
		};
		// % protected region % [Modify the order by field here] end
	}

	// % protected region % [Modify props to the crud options here for attribute 'Name'] off begin
	@Validators.Required()
	@observable
	@attribute<RegionEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;
	// % protected region % [Modify props to the crud options here for attribute 'Name'] end

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Countries'] off begin
		name: 'Countriess',
		displayType: 'reference-multicombobox',
		order: 20,
		referenceTypeFunc: () => Models.CountryEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'countriess',
			oppositeEntity: () => Models.CountryEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Countries'] end
	})
	public countriess: Models.CountryEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Venues'] off begin
		name: 'Venuess',
		displayType: 'reference-multicombobox',
		order: 30,
		referenceTypeFunc: () => Models.VenueEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'venuess',
			oppositeEntity: () => Models.VenueEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Venues'] end
	})
	public venuess: Models.VenueEntity[] = [];

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IRegionEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IRegionEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.countriess !== undefined && Array.isArray(attributes.countriess)) {
				for (const model of attributes.countriess) {
					if (model instanceof Models.CountryEntity) {
						this.countriess.push(model);
					} else {
						this.countriess.push(new Models.CountryEntity(model));
					}
				}
			}
			if (attributes.venuess !== undefined && Array.isArray(attributes.venuess)) {
				for (const model of attributes.venuess) {
					if (model instanceof Models.VenueEntity) {
						this.venuess.push(model);
					} else {
						this.venuess.push(new Models.VenueEntity(model));
					}
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		countriess {
			${Models.CountryEntity.getAttributes().join('\n')}
		}
		venuess {
			${Models.VenueEntity.getAttributes().join('\n')}
			${Models.VenueEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			countriess: {},
			venuess: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'countriess',
							'venuess',
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.name;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(RegionEntity.prototype, 'created');
CRUD(modifiedAttr)(RegionEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
